import {
  useGetConsultantTagsQuery,
  useGetAllConsultantTagsLazyQuery,
  useAddConsultantTagMutation,
  useRemoveConsultantTagMutation
} from "@bonliva-crm/graphql";
import {
  CirclePlusIcon,
  CloseIcon,
  Color,
  MultiCreatableGroupedSelect,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { Box, Chip, IconButton, Typography, makeStyles } from "@material-ui/core";
import { transparentize } from "polished";
import { useEffect, useState } from "react";
import { FetchPolicy } from "@apollo/client";
import { useLocale } from "@hooks";

type Props = {
  consultantId: string;
  fetchPolicy?: FetchPolicy;
};

type Option = { label: string; value: string };

const MAX_TAGS = 10;

export function ConsultantTags(props: Props) {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const { locale } = useLocale();

  const [canAddTags, setCanAddTags] = useState(false);
  const [editing, setEditing] = useState(false);

  const query = useGetConsultantTagsQuery({
    variables: {
      id: props.consultantId
    },
    fetchPolicy: props.fetchPolicy ?? "cache-only"
  });

  const [fetchAllTags, allTagsQuery] = useGetAllConsultantTagsLazyQuery({
    fetchPolicy: "cache-and-network"
  });

  const [_mutateAdd] = useAddConsultantTagMutation({
    onCompleted: () => {
      snack.showSuccess(locale.Saved);
    },
    onError: (e) => {
      console.error(e);
      snack.showError(locale.addError(locale.tag.toLowerCase()));
    }
  });

  const [_mutateRemove] = useRemoveConsultantTagMutation({
    onCompleted: () => {
      snack.showSuccess(locale.Saved);
    },
    onError: (e) => {
      console.error(e);
      snack.showError(locale.removeError(locale.tag.toLowerCase()));
    }
  });

  useEffect(() => {
    if (!query.data?.consultantById) {
      setCanAddTags(false);
    }

    if (query.data.consultantById) {
      setCanAddTags(query.data.consultantById.tags.length < MAX_TAGS);
    }
  }, [query.data]);

  const edit = () => {
    if (canAddTags) {
      fetchAllTags();
      setEditing(true);
    }
  };

  const consultantTagsOptions = (query.data.consultantById?.tags || []).map((tag) => ({
    label: tag,
    value: tag
  }));

  const allTagsOptions = (allTagsQuery.data?.getAllConsultantTags.tags || []).map(
    (tag) => ({ label: tag, value: tag })
  );

  const inputOptions = [
    {
      label: locale.alreadyAddedTags,
      options: allTagsOptions
    }
  ];

  const removeTag = (tag: string) => {
    _mutateRemove({
      variables: { id: props.consultantId, tag: tag },
      optimisticResponse: (vars) => {
        return {
          removeConsultantTag: {
            __typename: "Consultant",
            _id: props.consultantId,
            tags: query.data.consultantById.tags.filter((t) => t !== vars.tag)
          }
        };
      }
    });
  };

  const addTag = (remove: string) => {
    _mutateAdd({
      variables: { id: props.consultantId, tag: remove },
      optimisticResponse: (vars) => {
        return {
          addConsultantTag: {
            __typename: "Consultant",
            _id: props.consultantId,
            tags: [...query.data.consultantById.tags, vars.tag]
          }
        };
      }
    });
  };

  const onSelectionChange = async (value: Option[]) => {
    const newTag = value.find(
      (selectedTag) =>
        !consultantTagsOptions.some(
          (originalTag) => originalTag.value === selectedTag.value
        )
    );
    const removedTag = consultantTagsOptions.find(
      (originalTag) =>
        !value.some((selectedTag) => selectedTag.value === originalTag.value)
    );

    newTag && addTag(newTag.value);
    removedTag && removeTag(removedTag.value);

    const numberOfTags =
      query.data?.consultantById.tags.length + (newTag ? 1 : 0) - (removedTag ? 1 : 0);
    if (numberOfTags >= MAX_TAGS) {
      setCanAddTags(false);
      setEditing(false);
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="textSecondary" className={classes.sectionTitle}>
        {locale.tags}
      </Typography>

      {editing && (
        <MultiCreatableGroupedSelect<Option>
          placeholder=""
          isMulti={true}
          options={inputOptions}
          addNewLabel={locale.addNewTag}
          value={consultantTagsOptions}
          onChange={onSelectionChange}
          isClearable={false}
          onBlur={() => setEditing(false)}
          autoFocus={true}
          styleProps={{ menu: { showBorder: true }, control: { showBorder: true } }}
          openMenuOnFocus={true}
          closeMenuOnSelect={false}
        />
      )}

      {!editing && (
        <Box className={classes.chipContainer}>
          {query.data?.consultantById.tags.map((tag, index) => (
            <Chip
              key={`tag${index}`}
              size="small"
              variant="outlined"
              label={
                <Typography variant="subtitle1" style={{ color: Color.Body }}>
                  {tag}
                </Typography>
              }
              deleteIcon={<CloseIcon style={{ color: Color.Headings }} />}
              onDelete={() => removeTag(tag)}
              style={{
                padding: "2px 4px 2px 2px",
                backgroundColor: transparentize(0.75, Color.DoveBlue)
              }}
            />
          ))}

          <IconButton
            style={{ alignSelf: "center", padding: "0" }}
            onClick={() => edit()}
            disabled={!canAddTags}
          >
            <CirclePlusIcon
              style={{
                color: !canAddTags ? Color.Gray : undefined
              }}
            />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.25rem",
    padding: "0",
    border: "1px solid transparent"
  },
  sectionTitle: {
    marginBottom: "0.75rem",
    textTransform: "uppercase"
  }
}));
